import React, { useMemo, useState } from "react";
import { Gauge, Euro, Info, FileText, X, Plus, AlertTriangle, Briefcase, Stethoscope, Landmark, Sparkles } from "lucide-react";

const MONTHS = ["Janv", "Févr", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];
const YEAR = new Date().getFullYear();

const SEED_ACTS = [
  { id: "d1", m: 0, day: 12, p: "M.L." }, { id: "d2", m: 0, day: 26, p: "A.B." },
  { id: "d3", m: 1, day: 5, p: "M.L." }, { id: "d4", m: 2, day: 14, p: "C.R." },
  { id: "d5", m: 2, day: 21, p: "M.L." }, { id: "d6", m: 3, day: 3, p: "M.L." },
];

const CONTRACT_TYPES = [
  { id: "ordre", icon: Landmark, label: "Déclaration télésoin à l'Ordre", desc: "Atteste que vous exercez via une plateforme référencée (HDS, référentiel socle)." },
  { id: "travail", icon: Briefcase, label: "Contrat de travail (salarié)", desc: "CDI/CDD secrétaires et infirmiers avec clauses télésanté + RGPD." },
  { id: "collaboration", icon: Stethoscope, label: "Contrat de collaboration", desc: "Confrère / consœur exerçant sur votre plateforme, redevance incluse." },
];

export default function SandboxTelesoin({ brand }) {
  const [ca, setCa] = useState(90000);
  const [tariff, setTariff] = useState(25);
  const [acts, setActs] = useState(SEED_ACTS);
  const [preview, setPreview] = useState(null);

  const maxCa = Math.round(ca * 0.2);
  const maxActs = tariff > 0 ? Math.floor(maxCa / tariff) : 0;
  const monthly = Math.floor(maxActs / 12);
  const perPatient = useMemo(() => {
    const m = {};
    acts.forEach((a) => { m[a.p] = (m[a.p] || 0) + 1; });
    return m;
  }, [acts]);
  const pct = maxActs > 0 ? Math.min(100, Math.round((acts.length / maxActs) * 100)) : 0;

  const addDemoAct = (mIdx) => {
    const pool = ["J.D.", "P.M.", "S.T.", "M.L."];
    const p = pool[Math.floor(Math.random() * pool.length)];
    if ((perPatient[p] || 0) >= 4) {
      window.alert(`Blocage réglementaire : le patient ${p} a déjà 4 actes à distance cette année (maximum 4/an/patient).`);
      return;
    }
    setActs((prev) => [...prev, { id: `x${Date.now()}`, m: mIdx, day: 1 + Math.floor(Math.random() * 28), p }]);
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="sandbox-telesoin">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--bp)" }}>
        <Gauge size={13} /> Quota télésoin & contrats
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9.5px] normal-case tracking-normal">
          <Sparkles size={9} /> Démo interactive
        </span>
      </div>
      <h2 className="mt-2 font-display text-slate-900 text-2xl font-semibold">
        Pilotez vos plafonds réglementaires de télésanté
      </h2>

      <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-3.5 flex items-start gap-2.5 text-[12px] text-slate-700 leading-relaxed">
        <Info size={14} className="text-sky-600 mt-0.5 shrink-0" />
        <span><b>Règles Assurance Maladie</b> : max <b>20 % de votre CA annuel</b> en actes à distance,
        et max <b>4 actes/an pour un même patient</b> (service-public.fr · ameli.fr). {brand?.name || "Votre plateforme"} les surveille pour vous.</span>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">Chiffre d'affaires {YEAR - 1} (€)</span>
            <div className="relative">
              <Euro size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="number" value={ca} onChange={(e) => setCa(parseFloat(e.target.value) || 0)}
                     data-testid="sandbox-telesoin-ca"
                     className="w-full h-10 rounded-lg border border-slate-200 pl-9 pr-3 text-[14px] text-slate-900 outline-none focus:border-sky-400" />
            </div>
          </label>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">Tarif moyen par acte (€)</span>
            <input type="number" value={tariff} onChange={(e) => setTariff(parseFloat(e.target.value) || 0)}
                   data-testid="sandbox-telesoin-tariff"
                   className="w-full h-10 rounded-lg border border-slate-200 px-3 text-[14px] text-slate-900 outline-none focus:border-sky-400" />
          </label>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Plafond CA distance</div>
            <div className="font-display text-slate-900 text-xl mt-0.5" data-testid="sandbox-telesoin-maxca">{maxCa.toLocaleString("fr-FR")} €</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Télésoins / an</div>
            <div className="font-display text-slate-900 text-xl mt-0.5" data-testid="sandbox-telesoin-maxacts">{maxActs}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Recommandé / mois</div>
            <div className="font-display text-slate-900 text-xl mt-0.5">~{monthly}</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11.5px] mb-1">
            <span className="text-slate-500">Consommation {YEAR}</span>
            <span className="font-semibold" style={{ color: pct >= 80 ? "#B45309" : "var(--bp)" }}>{acts.length} / {maxActs}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct >= 80 ? "#B45309" : "var(--bp)" }} />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Planning annuel — cliquez pour positionner un acte</div>
        <div className="grid grid-cols-3 lg:grid-cols-4 gap-2.5">
          {MONTHS.map((m, i) => {
            const mActs = acts.filter((a) => a.m === i);
            const over = monthly > 0 && mActs.length > monthly;
            return (
              <div key={m} className={`rounded-lg border bg-white p-2.5 ${over ? "border-red-300" : "border-slate-200"}`}
                   data-testid={`sandbox-month-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-slate-800">{m}</span>
                  <span className={`text-[10px] font-semibold ${over ? "text-red-600" : "text-slate-500"}`}>{mActs.length}/{monthly}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1 min-h-[18px]">
                  {mActs.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-px rounded-full text-[9px] bg-sky-100 text-slate-800">
                      {a.day} · {a.p}
                      <button onClick={() => setActs((prev) => prev.filter((x) => x.id !== a.id))}
                              className="grid place-items-center h-3 w-3 rounded-full hover:bg-sky-200"><X size={7} /></button>
                    </span>
                  ))}
                </div>
                <button onClick={() => addDemoAct(i)} data-testid={`sandbox-month-add-${i}`}
                        className="mt-1 inline-flex items-center gap-0.5 text-[10px] hover:underline" style={{ color: "var(--bp)" }}>
                  <Plus size={9} /> Acte
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {Object.entries(perPatient).map(([p, n]) => (
            <span key={p} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] border ${
              n >= 4 ? "bg-red-50 border-red-300 text-red-600" : n === 3 ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
              {n >= 4 && <AlertTriangle size={10} />} {p} — {n}/4
            </span>
          ))}
        </div>
      </div>

      <div className="mt-7">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Contrats officiels en 1 clic</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {CONTRACT_TYPES.map((t) => (
            <button key={t.id} onClick={() => setPreview(t)} data-testid={`sandbox-contract-${t.id}`}
                    className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-sky-400 transition">
              <t.icon size={17} style={{ color: "var(--bp)" }} />
              <div className="mt-2 text-[13px] font-semibold text-slate-900">{t.label}</div>
              <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}
               data-testid="sandbox-contract-preview">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-900">
                <FileText size={15} style={{ color: "var(--bp)" }} /> {preview.label}
              </div>
              <button onClick={() => setPreview(null)} className="grid place-items-center h-7 w-7 rounded-full hover:bg-slate-100"><X size={14} /></button>
            </div>
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-700 leading-relaxed space-y-2">
              <p><b>Aperçu du modèle :</b> parties identifiées (RPPS/ADELI, SIRET), articles couvrant
              l'exercice via {brand?.name || "votre plateforme"} (hébergement HDS, Pro Santé Connect, MSSanté),
              consentement patient, plafonds 20 % du CA et 4 actes/an/patient, confidentialité RGPD,
              blocs de signature.</p>
              <p className="text-slate-500">Sur votre plateforme réelle : PDF pré-rempli téléchargeable
              et envoi par email au destinataire (Ordre, salarié, confrère) en un clic.</p>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setPreview(null)}
                      className="px-5 py-2 rounded-full text-white text-[13px] font-medium" style={{ background: "var(--bp)" }}>
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
